"use client";

import { c as _c } from "react/compiler-runtime";
import { useMemo, useState } from "react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useTranslation } from "react-i18next";
import type { OrganizationMetric } from "@/entities/organization/api/organization-metric-drilldown";
import type { OrganizationRanking, OrganizationRankingSortBy } from "@/entities/organization/api/organizations";
import { useOrganizationRankingsQuery } from "@/entities/organization/model/use-organization-rankings";
import type { OrganizationMetricDrilldownTarget } from "@/entities/organization/ui/organization-metric-chip";
import { OrganizationMetricDrilldownSheet } from "@/entities/organization/ui/organization-metric-drilldown-sheet";
import { OrganizationRankingRow } from "@/entities/organization/ui/organization-ranking-row";
import { OrganizationRankingRowMobile } from "@/entities/organization/ui/organization-ranking-row-mobile";
import { buildApiErrorMessage, type PaginatedResponse } from "@/shared/api/common";
import { ORGANIZATION_REGIONS, type OrganizationRegion } from "@/shared/config/constants";
import { useIsMobile } from "@/shared/hooks/use-mobile";
import { cn } from "@/shared/lib/utils";
import { Card, CardContent, CardHeader } from "@/shared/ui/card";
import { EmptyState } from "@/shared/ui/empty-state";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/shared/ui/select";
import { ServerPagination } from "@/shared/ui/server-pagination";
import { ListSkeleton } from "@/shared/ui/skeletons";
import { PublicRankingList } from "./public-ranking-list";
const DEFAULT_PAGE_SIZE = 20;
const PAYMENT_PERCENTAGE = 100;
const DRILLDOWN_METRICS: OrganizationMetric[] = ["exclusive", "cheapest", "promotion"];
function isDrilldownMetric(value: string | null): value is OrganizationMetric {
  return DRILLDOWN_METRICS.includes(value as OrganizationMetric);
}
function buildDrilldownTarget(organizationId: string, metric: OrganizationMetric, ranking?: OrganizationRanking): OrganizationMetricDrilldownTarget {
  const metrics = ranking?.metrics;
  if (metric === "exclusive") {
    return {
      organizationId,
      organizationName: ranking?.name ?? "",
      metric,
      expectedCount: metrics?.exclusive_medicines ?? 0,
      expectedPctLabel: null
    };
  }
  const ratio = metric === "cheapest" ? ranking?.ratios.cheapest_pct : ranking?.ratios.promotion_pct;
  return {
    organizationId,
    organizationName: ranking?.name ?? "",
    metric,
    expectedCount: (metric === "cheapest" ? metrics?.cheapest_count : metrics?.promotion_count) ?? 0,
    expectedPctLabel: ratio == null ? null : `${Math.round(ratio * 100)}%`
  };
}
export function OrganizationRankingsView(t0) {
  const $ = _c(145);
  const {
    variant: t1,
    initialData
  } = t0;
  const variant = t1 === undefined ? "authenticated" : t1;
  const {
    t
  } = useTranslation();
  const pathname = usePathname();
  const router = useRouter();
  const searchParams = useSearchParams();
  const isMobile = useIsMobile();
  const [region, setRegion] = useState("all");
  const [sortBy, setSortBy] = useState("overall");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const isPublic = variant === "public";
  let t2;
  if ($[0] !== t) {
    t2 = t("organizations.ratings.sortOptions.overall");
    $[0] = t;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== t2) {
    t3 = {
      value: "overall",
      label: t2
    };
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t) {
    t4 = t("organizations.ratings.sortOptions.cheapest_pct");
    $[4] = t;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== t4) {
    t5 = {
      value: "cheapest_pct",
      label: t4
    };
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== t) {
    t6 = t("organizations.ratings.sortOptions.variety");
    $[8] = t;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t6) {
    t7 = {
      value: "variety",
      label: t6
    };
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== t) {
    t8 = t("organizations.ratings.sortOptions.medicines");
    $[12] = t;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== t8) {
    t9 = {
      value: "medicines",
      label: t8
    };
    $[14] = t8;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== t) {
    t10 = t("organizations.ratings.sortOptions.promotion_pct");
    $[16] = t;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== t10) {
    t11 = {
      value: "promotion_pct",
      label: t10
    };
    $[18] = t10;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] !== t) {
    t12 = t("organizations.ratings.sortOptions.freshness");
    $[20] = t;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  let t13;
  if ($[22] !== t12) {
    t13 = {
      value: "freshness",
      label: t12
    };
    $[22] = t12;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  let t14;
  if ($[24] !== t11 || $[25] !== t13 || $[26] !== t3 || $[27] !== t5 || $[28] !== t7 || $[29] !== t9) {
    t14 = [t3, t5, t7, t9, t11, t13];
    $[24] = t11;
    $[25] = t13;
    $[26] = t3;
    $[27] = t5;
    $[28] = t7;
    $[29] = t9;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  const sortOptions = t14;
  const t15 = region === "all" ? undefined : region;
  let t16;
  if ($[31] !== initialData || $[32] !== page || $[33] !== pageSize || $[34] !== sortBy || $[35] !== t15) {
    t16 = {
      region: t15,
      paymentPercentage: PAYMENT_PERCENTAGE,
      sortBy,
      page,
      limit: pageSize,
      initialData
    };
    $[31] = initialData;
    $[32] = page;
    $[33] = pageSize;
    $[34] = sortBy;
    $[35] = t15;
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  const query = useOrganizationRankingsQuery(t16);
  let rows;
  let t17;
  if ($[37] !== isPublic || $[38] !== query.data?.data || $[39] !== searchParams) {
    rows = query.data?.data ?? [];
    const organizationId = searchParams.get("organization");
    const metricParam = searchParams.get("metric");
    t17 = !isPublic && organizationId && isDrilldownMetric(metricParam) ? buildDrilldownTarget(organizationId, metricParam, rows.find(ranking => ranking.organization_id === organizationId)) : null;
    $[37] = isPublic;
    $[38] = query.data?.data;
    $[39] = searchParams;
    $[40] = rows;
    $[41] = t17;
  } else {
    rows = $[40];
    t17 = $[41];
  }
  const drilldown = t17;
  const pagination = query.data?.pagination;
  const total = pagination?.total ?? 0;
  const totalPages = pagination?.totalPages ?? 0;
  const firstRank = (page - 1) * pageSize;
  let t18;
  if ($[42] !== query.error || $[43] !== query.isError) {
    t18 = query.isError ? buildApiErrorMessage(query.error, "Unable to load organization rankings.") : null;
    $[42] = query.error;
    $[43] = query.isError;
    $[44] = t18;
  } else {
    t18 = $[44];
  }
  const errorMessage = t18;
  let t19;
  if ($[45] !== isPublic || $[46] !== pathname || $[47] !== router || $[48] !== searchParams) {
    t19 = target => {
      if (isPublic) {
        return;
      }
      const params = new URLSearchParams(searchParams.toString());
      if (target) {
        params.set("organization", target.organizationId);
        params.set("metric", target.metric);
      } else {
        params.delete("organization");
        params.delete("metric");
      }
      const queryString = params.toString();
      router.replace(queryString ? `${pathname}?${queryString}` : pathname, {
        scroll: false
      });
    };
    $[45] = isPublic;
    $[46] = pathname;
    $[47] = router;
    $[48] = searchParams;
    $[49] = t19;
  } else {
    t19 = $[49];
  }
  const setDrilldown = t19;
  let t20;
  if ($[50] !== setDrilldown) {
    t20 = value => {
      setRegion(value as OrganizationRegion | "all");
      setPage(1);
      setDrilldown(null);
    };
    $[50] = setDrilldown;
    $[51] = t20;
  } else {
    t20 = $[51];
  }
  const t21 = isMobile ? "h-9 flex-1" : "h-8 w-42.5";
  let t22;
  if ($[52] !== t21) {
    t22 = cn("text-xs", t21);
    $[52] = t21;
    $[53] = t22;
  } else {
    t22 = $[53];
  }
  const t23 = isMobile ? "default" : "sm";
  let t24;
  if ($[54] !== t) {
    t24 = t("organizations.ratings.allRegions");
    $[54] = t;
    $[55] = t24;
  } else {
    t24 = $[55];
  }
  let t25;
  if ($[56] !== t24) {
    t25 = <SelectValue placeholder={t24} />;
    $[56] = t24;
    $[57] = t25;
  } else {
    t25 = $[57];
  }
  let t26;
  if ($[58] !== t22 || $[59] !== t23 || $[60] !== t25) {
    t26 = <SelectTrigger className={t22} size={t23}>{t25}</SelectTrigger>;
    $[58] = t22;
    $[59] = t23;
    $[60] = t25;
    $[61] = t26;
  } else {
    t26 = $[61];
  }
  let t27;
  if ($[62] !== t) {
    t27 = t("organizations.ratings.allRegions");
    $[62] = t;
    $[63] = t27;
  } else {
    t27 = $[63];
  }
  let t28;
  if ($[64] !== t27) {
    t28 = <SelectItem value="all">{t27}</SelectItem>;
    $[64] = t27;
    $[65] = t28;
  } else {
    t28 = $[65];
  }
  let t29;
  if ($[66] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = ORGANIZATION_REGIONS.map(_temp2);
    $[66] = t29;
  } else {
    t29 = $[66];
  }
  let t30;
  if ($[67] !== t28) {
    t30 = <SelectContent>{t28}{t29}</SelectContent>;
    $[67] = t28;
    $[68] = t30;
  } else {
    t30 = $[68];
  }
  let t31;
  if ($[69] !== region || $[70] !== t20 || $[71] !== t26 || $[72] !== t30) {
    t31 = <Select value={region} onValueChange={t20}>{t26}{t30}</Select>;
    $[69] = region;
    $[70] = t20;
    $[71] = t26;
    $[72] = t30;
    $[73] = t31;
  } else {
    t31 = $[73];
  }
  const regionSelect = t31;
  let t32;
  if ($[74] === Symbol.for("react.memo_cache_sentinel")) {
    t32 = value_0 => {
      setSortBy(value_0 as OrganizationRankingSortBy);
      setPage(1);
    };
    $[74] = t32;
  } else {
    t32 = $[74];
  }
  const t33 = isMobile ? "h-9 flex-1" : "h-8 w-45";
  let t34;
  if ($[75] !== t33) {
    t34 = cn("text-xs", t33);
    $[75] = t33;
    $[76] = t34;
  } else {
    t34 = $[76];
  }
  const t35 = isMobile ? "default" : "sm";
  let t36;
  if ($[77] === Symbol.for("react.memo_cache_sentinel")) {
    t36 = <SelectValue />;
    $[77] = t36;
  } else {
    t36 = $[77];
  }
  let t37;
  if ($[78] !== t34 || $[79] !== t35) {
    t37 = <SelectTrigger className={t34} size={t35}>{t36}</SelectTrigger>;
    $[78] = t34;
    $[79] = t35;
    $[80] = t37;
  } else {
    t37 = $[80];
  }
  let t38;
  if ($[81] !== sortOptions) {
    t38 = <SelectContent>{sortOptions.map(_temp3)}</SelectContent>;
    $[81] = sortOptions;
    $[82] = t38;
  } else {
    t38 = $[82];
  }
  let t39;
  if ($[83] !== sortBy || $[84] !== t37 || $[85] !== t38) {
    t39 = <Select value={sortBy} onValueChange={t32}>{t37}{t38}</Select>;
    $[83] = sortBy;
    $[84] = t37;
    $[85] = t38;
    $[86] = t39;
  } else {
    t39 = $[86];
  }
  const sortSelect = t39;
  let t40;
  if ($[87] !== errorMessage || $[88] !== firstRank || $[89] !== isMobile || $[90] !== isPublic || $[91] !== query.isLoading || $[92] !== rows || $[93] !== setDrilldown || $[94] !== t) {
    t40 = query.isLoading ? <ListSkeleton count={6} height={isMobile ? "h-32" : "h-24"} className="p-3" /> : errorMessage ? <EmptyState title={t("organizations.ratings.errorTitle")} description={errorMessage} /> : rows.length === 0 ? <EmptyState title={t("organizations.ratings.emptyTitle")} description={t("organizations.ratings.emptyDescription")} /> : isPublic ? <PublicRankingList rows={rows} firstRank={firstRank} isMobile={isMobile} /> : <div className={isMobile ? "flex flex-col gap-2 py-1" : ""}>{rows.map((ranking_0, index) => isMobile ? <OrganizationRankingRowMobile key={ranking_0.organization_id} rank={firstRank + index + 1} ranking={ranking_0} onMetricClick={isPublic ? undefined : setDrilldown} /> : <OrganizationRankingRow key={ranking_0.organization_id} rank={firstRank + index + 1} ranking={ranking_0} onMetricClick={isPublic ? undefined : setDrilldown} />)}</div>;
    $[87] = errorMessage;
    $[88] = firstRank;
    $[89] = isMobile;
    $[90] = isPublic;
    $[91] = query.isLoading;
    $[92] = rows;
    $[93] = setDrilldown;
    $[94] = t;
    $[95] = t40;
  } else {
    t40 = $[95];
  }
  const list = t40;
  let t41;
  if ($[96] !== drilldown || $[97] !== isPublic || $[98] !== region || $[99] !== setDrilldown) {
    t41 = isPublic ? null : <OrganizationMetricDrilldownSheet target={drilldown} region={region === "all" ? undefined : region} paymentPercentage={PAYMENT_PERCENTAGE} onOpenChange={open => {
      if (!open) {
        setDrilldown(null);
      }
    }} />;
    $[96] = drilldown;
    $[97] = isPublic;
    $[98] = region;
    $[99] = setDrilldown;
    $[100] = t41;
  } else {
    t41 = $[100];
  }
  const drilldownSheet = t41;
  if (isMobile) {
    let t42;
    if ($[101] !== isPublic || $[102] !== t) {
      t42 = !isPublic ? <div className="flex shrink-0 flex-col gap-0.5 px-1"><h1 className="text-foreground text-lg font-semibold tracking-tight">{t("organizations.ratings.title")}</h1><p className="text-muted-foreground text-[11.5px] leading-snug">{t("organizations.ratings.description")}</p></div> : null;
      $[101] = isPublic;
      $[102] = t;
      $[103] = t42;
    } else {
      t42 = $[103];
    }
    let t43;
    if ($[104] !== regionSelect || $[105] !== sortSelect) {
      t43 = <div className="bg-card/80 ring-foreground/8 flex shrink-0 items-center gap-2 rounded-xl px-3 py-2 shadow-xs ring-1">{regionSelect}{sortSelect}</div>;
      $[104] = regionSelect;
      $[105] = sortSelect;
      $[106] = t43;
    } else {
      t43 = $[106];
    }
    let t44;
    if ($[107] !== list) {
      t44 = <div className="-mx-1 flex min-h-0 flex-1 flex-col overflow-y-auto px-1">{list}</div>;
      $[107] = list;
      $[108] = t44;
    } else {
      t44 = $[108];
    }
    let t45;
    if ($[109] !== page || $[110] !== pageSize || $[111] !== totalPages) {
      t45 = totalPages > 1 ? <div className="bg-card/80 ring-foreground/8 shrink-0 rounded-xl px-3 py-2 shadow-xs ring-1"><ServerPagination currentPage={page} totalPages={totalPages} pageSize={pageSize} onPageChange={setPage} onPageSizeChange={size => {
          setPageSize(size);
          setPage(1);
        }} /></div> : null;
      $[109] = page;
      $[110] = pageSize;
      $[111] = totalPages;
      $[112] = t45;
    } else {
      t45 = $[112];
    }
    let t46;
    if ($[113] !== drilldownSheet || $[114] !== t42 || $[115] !== t43 || $[116] !== t44 || $[117] !== t45) {
      t46 = <div className="flex h-full min-h-0 flex-1 flex-col gap-3">{t42}{t43}{t44}{t45}{drilldownSheet}</div>;
      $[113] = drilldownSheet;
      $[114] = t42;
      $[115] = t43;
      $[116] = t44;
      $[117] = t45;
      $[118] = t46;
    } else {
      t46 = $[118];
    }
    return t46;
  }
  let t42;
  if ($[119] !== isPublic || $[120] !== t) {
    t42 = !isPublic ? <div className="flex shrink-0 flex-col gap-1"><p className="text-muted-foreground text-sm">{t("organizations.ratings.description")}</p></div> : null;
    $[119] = isPublic;
    $[120] = t;
    $[121] = t42;
  } else {
    t42 = $[121];
  }
  let t43;
  if ($[122] !== t || $[123] !== total) {
    t43 = t("organizations.ratings.count", {
      count: total
    });
    $[122] = t;
    $[123] = total;
    $[124] = t43;
  } else {
    t43 = $[124];
  }
  let t44;
  if ($[125] !== t43) {
    t44 = <div className="text-muted-foreground ml-auto text-xs">{t43}</div>;
    $[125] = t43;
    $[126] = t44;
  } else {
    t44 = $[126];
  }
  let t45;
  if ($[127] !== regionSelect || $[128] !== sortSelect || $[129] !== t44) {
    t45 = <CardHeader className="border-border/60 shrink-0 gap-3 border-b px-4 py-3!"><div className="flex flex-wrap items-center gap-2">{regionSelect}{sortSelect}{t44}</div></CardHeader>;
    $[127] = regionSelect;
    $[128] = sortSelect;
    $[129] = t44;
    $[130] = t45;
  } else {
    t45 = $[130];
  }
  let t46;
  if ($[131] !== list) {
    t46 = <CardContent className="min-h-0 flex-1 overflow-y-auto p-0">{list}</CardContent>;
    $[131] = list;
    $[132] = t46;
  } else {
    t46 = $[132];
  }
  let t47;
  if ($[133] !== page || $[134] !== pageSize || $[135] !== totalPages) {
    t47 = totalPages > 1 ? <div className="border-border/60 shrink-0 border-t px-4 py-2"><ServerPagination currentPage={page} totalPages={totalPages} pageSize={pageSize} onPageChange={setPage} onPageSizeChange={size_0 => {
        setPageSize(size_0);
        setPage(1);
      }} /></div> : null;
    $[133] = page;
    $[134] = pageSize;
    $[135] = totalPages;
    $[136] = t47;
  } else {
    t47 = $[136];
  }
  let t48;
  if ($[137] !== t45 || $[138] !== t46 || $[139] !== t47) {
    t48 = <Card className="bg-card/80 ring-foreground/8 flex min-h-0 flex-1 flex-col rounded-xl border-none shadow-xs ring-1">{t45}{t46}{t47}</Card>;
    $[137] = t45;
    $[138] = t46;
    $[139] = t47;
    $[140] = t48;
  } else {
    t48 = $[140];
  }
  let t49;
  if ($[141] !== drilldownSheet || $[142] !== t42 || $[143] !== t48) {
    t49 = <div className="flex h-full min-h-0 flex-1 flex-col gap-4">{t42}{t48}{drilldownSheet}</div>;
    $[141] = drilldownSheet;
    $[142] = t42;
    $[143] = t48;
    $[144] = t49;
  } else {
    t49 = $[144];
  }
  return t49;
}
function _temp3(opt) {
  return <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>;
}
function _temp2(r) {
  return <SelectItem key={r} value={r}>{r.replace(/_/g, " ").replace(/^./, _temp)}</SelectItem>;
}
function _temp(c) {
  return c.toUpperCase();
}